import { readStoredSession } from "./auth-client";
import { clientEnv } from "../env";

export type ReaPlatform = "web" | "ios" | "android" | "windows" | "macos" | "other";
export type ReaKind = "button" | "input" | "toggle" | "tab" | "menu" | "image" | "text" | "list";
export type ReaEvidence = "observed" | "documentation" | "hypothesis";
export type ReaElement = { kind: ReaKind; label: string; action: string; evidence: ReaEvidence };
export type ReaScreen = { name: string; description: string; elements: ReaElement[] };
export type ReaManifest = {
  appName: string;
  platform: ReaPlatform;
  goal: string;
  rightsConfirmed: boolean;
  screens: ReaScreen[];
};
export type ReaReport = {
  analysisId: string;
  reference: { appName: string; platform: ReaPlatform };
  targetModules: string[];
  inventory: Array<{ screen: string; element: string; action: string | null;
    evidence: ReaEvidence; needsBehaviorDefinition: boolean }>;
  undefinedActions: number;
  evidenceNote: string;
  needsReview: string[];
  verifiedImplementation: false;
  expiresInSeconds: number;
};
export type ReaGenerated = {
  analysisId: string;
  artifact: { filename: string; mimeType: string; content: string };
  status: string;
  executed: false;
  mergedIntoOneCore: false;
  deploymentPerformed: false;
  instructions: string;
};
export type ReaStatus = {
  name: string;
  version: string;
  status: string;
  screenshotVision: boolean;
  automaticIntegration: boolean;
  retentionMinutes: number;
};

export type ReaAgentStepId = "architect" | "builder" | "verifier" | "guard";
export type ReaAgentJobStatus = "queued" | "running" | "completed" | "needs_attention" | "failed" | "cancelled";
export type ReaAgentStep = {
  id: ReaAgentStepId;
  name: string;
  status: "pending" | "running" | "completed" | "failed" | "cancelled";
  detail?: string;
};
export type ReaAgentJob = {
  id: string;
  status: ReaAgentJobStatus;
  createdAt: string;
  updatedAt: string;
  goal: string;
  steps: ReaAgentStep[];
  report?: ReaReport;
  generated?: ReaGenerated;
  verification?: {
    passed: boolean;
    checks: Array<{ id: string; label: string; passed: boolean; detail: string }>;
    pending: string[];
  };
  error?: string;
  externalRequests: 0;
  prototypeExecuted: false;
  deploymentPerformed: false;
};
export type ReaAgentRegistry = {
  name: string;
  version: string;
  agents: Array<{ id: ReaAgentStepId; name: string; task: string }>;
  execution: "local";
  externalProviders: false;
  persistentStorage: false;
  retentionMinutes: number;
  concurrency: number;
  activeJobs: number;
  queuedJobs: number;
};

export type ReaAgentProfile = {
  id: string;
  displayName: string;
  description: string;
};
export type ReaAgentsCatalog = {
  schemaVersion: 1;
  profiles: ReaAgentProfile[];
  engine: {
    provider: "local";
    configured: boolean;
    model: string | null;
    connectionVerified: false;
  };
  limits: { maxTaskBytes: number; maxActive: 1 };
  attribution: {
    project: "Agency Agents";
    repository: string;
    commit: string;
    license: "MIT";
  };
};
export type ReaAgentRunInput = { agentId: string; task: string };
export type ReaAgentRunResult = {
  schemaVersion: 1;
  runId: string;
  agentId: string;
  agentName: string;
  status: "analysis_completed";
  text: string;
  model: string | null;
  externalApiBilling: false;
  credits: 0;
  durationMs: number;
  evidence: {
    source: "one_core_status";
    observedAt: string;
    aiCoreEnabled: boolean;
    localModelConfigured: boolean;
    model: string | null;
    concurrency: { active: number; maxActive: number };
    deviceConnectionVerified: false;
  };
  actions: {
    readOneCoreStatus: true;
    localModelResponded: true;
    testsExecuted: false;
    filesModified: false;
    codeExecuted: false;
    deployed: false;
  };
};

export class ReaClientError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = "ReaClientError";
  }
}

async function creatorRequest<T>(userId: string, endpoint: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  signal?.throwIfAborted();
  const session = readStoredSession();
  if (!session || session.user.id !== userId || session.user.role !== "creator"
      || !(Date.parse(session.expiresAt) > Date.now())) {
    throw new ReaClientError(401, "Accede con la sesión del Creador para trabajar con REA.");
  }
  // REA endpoints verify this Bearer on the server. Do not create guest sessions
  // or perform an additional, unabortable auth lookup on every polling request.
  // The specialist may spend up to 60 seconds in the existing local text adapter.
  const timeoutMs = endpoint === "agents/run" ? 75_000 : 20_000;
  const response = await fetch(clientEnv.apiBaseUrl + "/creator/rea/" + endpoint, {
    method: body === undefined ? "GET" : "POST",
    redirect: "error",
    signal: signal ? AbortSignal.any([signal, AbortSignal.timeout(timeoutMs)]) : AbortSignal.timeout(timeoutMs),
    headers: {
      Authorization: "Bearer " + session.sessionToken,
      ...(body === undefined ? {} : { "Content-Type": "application/json" })
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) })
  });
  const json: unknown = await response.json().catch(() => null);
  signal?.throwIfAborted();
  const currentSession = readStoredSession();
  if (!currentSession || currentSession.user.id !== userId
      || currentSession.sessionToken !== session.sessionToken || currentSession.user.role !== "creator") {
    throw new ReaClientError(401, "La sesión ha cambiado. Vuelve a abrir Agentes desde la cuenta del Creador.");
  }
  if (!response.ok) {
    const error = json && typeof json === "object" && "error" in json
      && typeof json.error === "string" ? json.error : "ONE CORE no pudo ejecutar REA.";
    throw new ReaClientError(response.status, error);
  }
  if (!json || typeof json !== "object") throw new ReaClientError(502, "Respuesta de REA no válida. Consulta de nuevo el estado del trabajo.");
  return json as T;
}

export function getReaStatus(userId: string, signal?: AbortSignal) {
  return creatorRequest<ReaStatus>(userId, "status", undefined, signal);
}
export function analyzeRea(userId: string, manifest: ReaManifest, signal?: AbortSignal) {
  return creatorRequest<ReaReport>(userId, "analyze", manifest, signal);
}
export function generateRea(userId: string, analysisId: string, signal?: AbortSignal) {
  return creatorRequest<ReaGenerated>(userId, "generate", { analysisId }, signal);
}
export function getReaAgentTeam(userId: string, signal?: AbortSignal) {
  return creatorRequest<ReaAgentRegistry>(userId, "team", undefined, signal);
}
export function listReaJobs(userId: string, signal?: AbortSignal) {
  return creatorRequest<{ jobs: ReaAgentJob[] }>(userId, "jobs", undefined, signal);
}
export function createReaJob(userId: string, manifest: ReaManifest, signal?: AbortSignal) {
  return creatorRequest<ReaAgentJob>(userId, "jobs", manifest, signal);
}
export function getReaJob(userId: string, jobId: string, signal?: AbortSignal) {
  return creatorRequest<ReaAgentJob>(userId, "jobs/" + encodeURIComponent(jobId), undefined, signal);
}
export function cancelReaJob(userId: string, jobId: string, signal?: AbortSignal) {
  return creatorRequest<ReaAgentJob>(userId, "jobs/" + encodeURIComponent(jobId) + "/cancel", {}, signal);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
function isModelName(value: unknown): value is string | null {
  return value === null || typeof value === "string";
}

export async function getReaAgents(userId: string, signal?: AbortSignal): Promise<ReaAgentsCatalog> {
  const result = await creatorRequest<unknown>(userId, "agents", undefined, signal);
  if (!isRecord(result) || result.schemaVersion !== 1
    || !Array.isArray(result.profiles) || result.profiles.length === 0
    || !result.profiles.every(profile => isRecord(profile)
      && typeof profile.id === "string" && profile.id.length > 0
      && typeof profile.displayName === "string" && typeof profile.description === "string")
    || new Set(result.profiles.map(profile => profile.id)).size !== result.profiles.length
    || !isRecord(result.engine) || result.engine.provider !== "local"
    || typeof result.engine.configured !== "boolean" || result.engine.connectionVerified !== false
    || !isModelName(result.engine.model)
    || !isRecord(result.limits) || typeof result.limits.maxTaskBytes !== "number"
    || !Number.isSafeInteger(result.limits.maxTaskBytes) || result.limits.maxTaskBytes < 1
    || result.limits.maxActive !== 1
    || !isRecord(result.attribution) || result.attribution.project !== "Agency Agents"
    || result.attribution.license !== "MIT" || typeof result.attribution.repository !== "string"
    || typeof result.attribution.commit !== "string") {
    throw new Error("No se pudo leer el catálogo de agentes de ONE CORE.");
  }
  return result as ReaAgentsCatalog;
}

export async function runReaAgent(
  userId: string, input: ReaAgentRunInput, signal?: AbortSignal
): Promise<ReaAgentRunResult> {
  const result = await creatorRequest<unknown>(userId, "agents/run", input, signal);
  if (!isRecord(result) || result.schemaVersion !== 1 || result.status !== "analysis_completed"
    || typeof result.runId !== "string" || result.agentId !== input.agentId
    || typeof result.agentName !== "string" || typeof result.text !== "string" || !result.text.trim()
    || !isModelName(result.model) || result.externalApiBilling !== false || result.credits !== 0
    || typeof result.durationMs !== "number" || !Number.isFinite(result.durationMs) || result.durationMs < 0
    || !isRecord(result.evidence) || result.evidence.source !== "one_core_status"
    || typeof result.evidence.observedAt !== "string" || typeof result.evidence.aiCoreEnabled !== "boolean"
    || typeof result.evidence.localModelConfigured !== "boolean" || !isModelName(result.evidence.model)
    || result.evidence.deviceConnectionVerified !== false || !isRecord(result.evidence.concurrency)
    || typeof result.evidence.concurrency.active !== "number"
    || typeof result.evidence.concurrency.maxActive !== "number"
    || !isRecord(result.actions) || result.actions.readOneCoreStatus !== true
    || result.actions.localModelResponded !== true || result.actions.testsExecuted !== false
    || result.actions.filesModified !== false || result.actions.codeExecuted !== false
    || result.actions.deployed !== false) {
    throw new Error("El agente no devolvió un análisis válido. Vuelve a intentarlo.");
  }
  return result as ReaAgentRunResult;
}
